'use client'

import { Input } from '@/components/forms/inputs/text/Input'
import { Progress } from '@/components/progress'
import { Textarea } from '@/components/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@/components/ui/card'
import { Badge } from '@/components/ui/common/Badge'
import { Button } from '@/components/ui/common/Button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import DashboardLayout from '@/views/dashboard/DashboardLayout'
import {
  BookOpen,
  Building,
  CalendarDays,
  CheckCircle,
  ChevronRight,
  Clock,
  Download,
  FileText,
  Heart,
  MapPin,
  Play,
  Route,
  School,
  Search,
  StickyNote,
  Target,
  Trophy,
  Users,
  Video
} from 'lucide-react'
import { useState } from 'react'

interface SchoolType {
  id: number
  name: string
  type: string
  location: string
  distance: string
  matchScore: number
  isFavorite: boolean
  subjects: string[]
  description: string
}

interface Event {
  id: number
  title: string
  date: string
  type: 'Rekrutacja' | 'Dzień otwarty' | 'Webinar'
  location?: string
  school?: string
  url?: string
}

interface Milestone {
  id: number
  title: string
  description: string
  completed: boolean
  date?: string
  note?: string
}

export default function MyPersonalDevelopmentPage() {
  const [favoriteSchools, setFavoriteSchools] = useState<number[]>([1, 3])
  const [locationFilter, setLocationFilter] = useState('all')
  const [typeFilter, setTypeFilter] = useState('all')
  const [subjectFilter, setSubjectFilter] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedMilestone, setSelectedMilestone] = useState<number | null>(null)
  const [milestoneNote, setMilestoneNote] = useState('')

  const schools: SchoolType[] = [
    {
      id: 1,
      name: 'XIV Liceum Ogólnokształcące im. Stanisława Staszica',
      type: 'Liceum',
      location: 'Warszawa',
      distance: '3.2 km',
      matchScore: 95,
      isFavorite: true,
      subjects: ['Matematyka', 'Fizyka', 'Informatyka'],
      description: 'Profil matematyczno-fizyczny z rozszerzonym programem informatyki'
    },
    {
      id: 2,
      name: 'II Liceum Ogólnokształcące z Oddziałami Dwujęzycznymi im. Stefana Batorego',
      type: 'Liceum',
      location: 'Warszawa',
      distance: '5.1 km',
      matchScore: 88,
      isFavorite: false,
      subjects: ['Język angielski', 'Historia', 'Geografia'],
      description: 'Profil humanistyczny z nauczaniem dwujęzycznym'
    },
    {
      id: 3,
      name: 'Technikum Mechatroniczne nr 1',
      type: 'Technikum',
      location: 'Warszawa',
      distance: '4.7 km',
      matchScore: 92,
      isFavorite: true,
      subjects: ['Informatyka', 'Elektronika', 'Mechanika'],
      description: 'Nowoczesne technikum z laboratoriami mechatronicznymi'
    },
    {
      id: 4,
      name: 'V Liceum Ogólnokształcące im. Augusta Witkowskiego',
      type: 'Liceum',
      location: 'Kraków',
      distance: '280 km',
      matchScore: 85,
      isFavorite: false,
      subjects: ['Biologia', 'Chemia', 'Matematyka'],
      description: 'Profil biologiczno-chemiczny, przygotowanie do studiów medycznych'
    }
  ]

  const events: Event[] = [
    {
      id: 1,
      title: 'Dzień otwarty - XIV LO im. Staszica',
      date: '2024-01-15',
      type: 'Dzień otwarty',
      location: 'Warszawa, Nowowiejska 37a',
      school: 'XIV LO im. Staszica'
    },
    {
      id: 2,
      title: 'Webinar: Jak przygotować się do rekrutacji?',
      date: '2024-01-10',
      type: 'Webinar',
      url: 'https://example.com/webinar'
    },
    {
      id: 3,
      title: 'Rozpoczęcie rekrutacji do szkół średnich',
      date: '2024-05-15',
      type: 'Rekrutacja'
    },
    {
      id: 4,
      title: 'Dzień otwarty - Technikum Mechatroniczne',
      date: '2024-01-20',
      type: 'Dzień otwarty',
      location: 'Warszawa, Wiśniowa 56',
      school: 'Technikum Mechatroniczne nr 1'
    }
  ]

  const milestones: Milestone[] = [
    {
      id: 1,
      title: 'Wykonanie testu predyspozycji',
      description: 'Ukończenie podstawowego testu określającego predyspozycje edukacyjne',
      completed: true,
      date: '2023-11-15',
      note: 'Wynik: 78% - profil matematyczno-fizyczny'
    },
    {
      id: 2,
      title: 'Analiza wyników i profilu',
      description: 'Przeanalizowanie wyników testów i określenie profilu ucznia',
      completed: true,
      date: '2023-11-20',
      note: 'Rekomendowane kierunki: informatyka, inżynieria'
    },
    {
      id: 3,
      title: 'Wybór 3 potencjalnych szkół',
      description: 'Wstępny wybór szkół na podstawie profilu i preferencji',
      completed: true,
      date: '2023-11-25',
      note: 'Wybrane: XIV LO Staszica, Technikum Mechatroniczne, II LO Batory'
    },
    {
      id: 4,
      title: 'Odwiedzenie dni otwartych',
      description: 'Uczestnictwo w dniach otwartych wybranych szkół',
      completed: false
    },
    {
      id: 5,
      title: 'Rozmowy z uczniami i nauczycielami',
      description: 'Zbieranie informacji od obecnych uczniów i kadry pedagogicznej',
      completed: false
    },
    {
      id: 6,
      title: 'Podjęcie ostatecznej decyzji',
      description: 'Wybór szkoły i profilu na podstawie zebranych informacji',
      completed: false
    }
  ]

  const filteredSchools = schools.filter((school) => {
    const matchesLocation = locationFilter === 'all' || school.location === locationFilter
    const matchesType = typeFilter === 'all' || school.type === typeFilter
    const matchesSubject =
      subjectFilter === 'all' ||
      school.subjects.some((subject) => subject.toLowerCase().includes(subjectFilter))
    const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase())

    return matchesLocation && matchesType && matchesSubject && matchesSearch
  })

  const toggleFavorite = (schoolId: number) => {
    if (favoriteSchools.includes(schoolId)) {
      setFavoriteSchools(favoriteSchools.filter((id) => id !== schoolId))
    } else {
      setFavoriteSchools([...favoriteSchools, schoolId])
    }
  }

  const addMilestoneNote = (milestoneId: number) => {
    // W rzeczywistej aplikacji tutaj byłoby zapisywanie do bazy danych
    console.log(`Dodano notatkę do kamienia milowego ${milestoneId}: ${milestoneNote}`)
    setSelectedMilestone(null)
    setMilestoneNote('')
  }

  const completedMilestones = milestones.filter((m) => m.completed).length
  const progressPercentage = (completedMilestones / milestones.length) * 100

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="mb-2 text-3xl font-bold text-gray-900">
              Mój rozwój edukacyjny
            </h1>
            <p className="text-gray-600">Śledź swój postęp w wyborze szkoły średniej</p>
          </div>

          {/* Progress Card */}
          <div className="mb-6 border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Postęp</h2>
                <p className="text-gray-600">Ukończone kamienie milowe</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-[#2bcaca]">
                  {Math.round(progressPercentage)}%
                </div>
                <div className="text-sm text-gray-500">ukończone</div>
              </div>
            </div>
            <Progress value={progressPercentage} className="mt-4 h-3" />
            <div className="mt-2 text-sm text-gray-600">
              {completedMilestones} z {milestones.length} kamieni milowych ukończonych
            </div>
          </div>

          <Tabs defaultValue="schools" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 rounded-none border border-gray-200 bg-white p-1 shadow-sm">
              <TabsTrigger
                value="schools"
                className="flex items-center space-x-2 data-[state=active]:bg-[#2bcaca] data-[state=active]:text-white">
                <Target className="h-4 w-4" />
                <span>🎯 Rekomendacje szkół</span>
              </TabsTrigger>
              <TabsTrigger
                value="path"
                className="flex items-center space-x-2 data-[state=active]:bg-[#2bcaca] data-[state=active]:text-white">
                <Route className="h-4 w-4" />
                <span>🛣️ Ścieżka edukacyjna</span>
              </TabsTrigger>
              <TabsTrigger
                value="guide"
                className="flex items-center space-x-2 data-[state=active]:bg-[#2bcaca] data-[state=active]:text-white">
                <BookOpen className="h-4 w-4" />
                <span>📚 Przewodnik</span>
              </TabsTrigger>
              <TabsTrigger
                value="events"
                className="flex items-center space-x-2 data-[state=active]:bg-[#2bcaca] data-[state=active]:text-white">
                <CalendarDays className="h-4 w-4" />
                <span>📅 Wydarzenia</span>
              </TabsTrigger>
            </TabsList>

            {/* Rekomendacje szkół */}
            <TabsContent value="schools" className="space-y-6">
              <Card className="rounded-none border border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Target className="h-5 w-5 text-[#2bcaca]" />
                    <span>Rekomendowane szkoły</span>
                  </CardTitle>
                  <CardDescription>
                    Szkoły dopasowane do Twojego profilu edukacyjnego
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Filtry */}
                  <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="relative">
                      <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-400" />
                      <Input
                        placeholder="Szukaj szkoły..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="border-gray-200 pl-10"
                      />
                    </div>
                    <Select value={locationFilter} onValueChange={setLocationFilter}>
                      <SelectTrigger className="border-gray-200">
                        <SelectValue placeholder="Lokalizacja" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Wszystkie miasta</SelectItem>
                        <SelectItem value="Warszawa">Warszawa</SelectItem>
                        <SelectItem value="Kraków">Kraków</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="border-gray-200">
                        <SelectValue placeholder="Typ szkoły" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Wszystkie typy</SelectItem>
                        <SelectItem value="Liceum">Liceum</SelectItem>
                        <SelectItem value="Technikum">Technikum</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={subjectFilter} onValueChange={setSubjectFilter}>
                      <SelectTrigger className="border-gray-200">
                        <SelectValue placeholder="Przedmiot" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Wszystkie przedmioty</SelectItem>
                        <SelectItem value="matematyka">Matematyka</SelectItem>
                        <SelectItem value="informatyka">Informatyka</SelectItem>
                        <SelectItem value="fizyka">Fizyka</SelectItem>
                        <SelectItem value="biologia">Biologia</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Lista szkół */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {filteredSchools.map((school) => (
                      <Card
                        key={school.id}
                        className="rounded-none border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                        <CardContent className="p-4">
                          <div className="mb-3 flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="line-clamp-2 font-semibold text-gray-900">
                                {school.name}
                              </h3>
                              <p className="text-sm text-gray-600">{school.type}</p>
                            </div>
                            <Button
                              size="sm"
                              className="h-auto bg-transparent p-1 hover:bg-transparent"
                              onClick={() => toggleFavorite(school.id)}>
                              <Heart
                                className={`h-5 w-5 border-white ${
                                  favoriteSchools.includes(school.id)
                                    ? 'fill-red-500 text-red-500'
                                    : 'text-gray-300'
                                }`}
                              />
                            </Button>
                          </div>

                          <div className="mb-4 space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <MapPin className="mr-1 h-4 w-4" />
                              {school.location} ({school.distance})
                            </div>
                            <p className="text-sm text-gray-600">{school.description}</p>
                          </div>

                          <div className="mb-4">
                            <div className="mb-2 flex items-center justify-between">
                              <span className="text-sm text-gray-600">Dopasowanie:</span>
                              <span className="font-semibold text-[#2bcaca]">
                                {school.matchScore}%
                              </span>
                            </div>
                            <Progress value={school.matchScore} className="h-2" />
                          </div>

                          <div className="mb-4">
                            <div className="mb-2 text-sm text-gray-600">
                              Kluczowe przedmioty:
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {school.subjects.map((subject, idx) => (
                                <Badge
                                  key={idx}
                                  variant="outline"
                                  className="border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca]">
                                  {subject}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              className="flex-1 bg-[#2bcaca] text-white hover:bg-[#229999]">
                              Zobacz szczegóły
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-200">
                              <School className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Ścieżka edukacyjna */}
            <TabsContent value="path" className="space-y-6">
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Route className="h-5 w-5 text-[#2bcaca]" />
                    <span>Moja ścieżka edukacyjna</span>
                  </CardTitle>
                  <CardDescription>
                    Wizualizacja Twojej drogi do wyboru szkoły
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {milestones.map((milestone, index) => (
                      <div key={milestone.id} className="flex items-start space-x-4">
                        <div className="flex flex-col items-center">
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full ${
                              milestone.completed
                                ? 'bg-[#2bcaca]/10 text-[#2bcaca]'
                                : 'bg-gray-100 text-gray-400'
                            }`}>
                            {milestone.completed ? (
                              <CheckCircle className="h-6 w-6" />
                            ) : (
                              <span className="font-semibold">{index + 1}</span>
                            )}
                          </div>
                          {index < milestones.length - 1 && (
                            <div
                              className={`h-16 w-0.5 ${milestone.completed ? 'bg-[#2bcaca]/30' : 'bg-gray-200'}`}></div>
                          )}
                        </div>

                        <div className="flex-1 pb-8">
                          <div className="mb-2 flex items-center justify-between">
                            <h3
                              className={`font-semibold ${milestone.completed ? 'text-gray-900' : 'text-gray-500'}`}>
                              {milestone.title}
                            </h3>
                            {milestone.completed && milestone.date && (
                              <span className="text-sm text-gray-500">
                                {new Date(milestone.date).toLocaleDateString('pl-PL')}
                              </span>
                            )}
                          </div>
                          <p className="mb-3 text-gray-600">{milestone.description}</p>

                          {milestone.note && (
                            <div className="mb-3 rounded-lg border border-[#2bcaca]/20 bg-[#2bcaca]/10 p-3">
                              <div className="mb-1 flex items-center space-x-2">
                                <StickyNote className="h-4 w-4 text-[#2bcaca]" />
                                <span className="text-sm font-medium text-[#2bcaca]">
                                  Notatka:
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{milestone.note}</p>
                            </div>
                          )}

                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-200"
                              onClick={() => setSelectedMilestone(milestone.id)}>
                              <StickyNote className="mr-1 h-4 w-4" />
                              {milestone.note ? 'Edytuj notatkę' : 'Dodaj notatkę'}
                            </Button>
                            {!milestone.completed && (
                              <Button
                                size="sm"
                                className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                                Oznacz jako ukończone
                              </Button>
                            )}
                          </div>

                          {selectedMilestone === milestone.id && (
                            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                              <Textarea
                                placeholder="Dodaj swoją notatkę..."
                                value={milestoneNote}
                                onChange={(e) => setMilestoneNote(e.target.value)}
                                className="mb-3"
                              />
                              <div className="flex space-x-2">
                                <Button
                                  size="sm"
                                  className="bg-[#2bcaca] text-white hover:bg-[#229999]"
                                  onClick={() => addMilestoneNote(milestone.id)}>
                                  Zapisz
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-gray-200"
                                  onClick={() => setSelectedMilestone(null)}>
                                  Anuluj
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Przewodnik i wsparcie */}
            <TabsContent value="guide" className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Card className="border border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <BookOpen className="h-5 w-5 text-[#2bcaca]" />
                      <span>FAQ - Najczęściej zadawane pytania</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="rounded-lg border border-gray-200 p-4">
                      <h3 className="mb-2 font-semibold">Jak wybrać szkołę średnią?</h3>
                      <p className="mb-3 text-sm text-gray-600">
                        Poznaj kluczowe kryteria wyboru szkoły średniej dopasowanej do
                        Twoich predyspozycji i planów na przyszłość.
                      </p>
                      <Button
                        size="sm"
                        variant="link"
                        className="h-auto p-0 text-[#2bcaca] hover:text-[#229999]">
                        Czytaj więcej <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-4">
                      <h3 className="mb-2 font-semibold">
                        Co oznacza profil humanistyczny/techniczny?
                      </h3>
                      <p className="mb-3 text-sm text-gray-600">
                        Dowiedz się, czym różnią się profile kształcenia i jakie
                        przedmioty są w nich kluczowe.
                      </p>
                      <Button
                        size="sm"
                        variant="link"
                        className="h-auto p-0 text-[#2bcaca] hover:text-[#229999]">
                        Czytaj więcej <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>

                    <div className="rounded-lg border border-gray-200 p-4">
                      <h3 className="mb-2 font-semibold">
                        Jak przygotować się do rekrutacji?
                      </h3>
                      <p className="mb-3 text-sm text-gray-600">
                        Praktyczne wskazówki dotyczące przygotowania dokumentów i procedur
                        rekrutacyjnych.
                      </p>
                      <Button
                        size="sm"
                        variant="link"
                        className="h-auto p-0 text-[#2bcaca] hover:text-[#229999]">
                        Czytaj więcej <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border border-gray-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <Video className="h-5 w-5 text-[#2bcaca]" />
                      <span>Materiały edukacyjne</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center space-x-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                      <Video className="h-8 w-8 text-[#2bcaca]" />
                      <div className="flex-1">
                        <h3 className="font-medium">
                          Jak przygotować się do rekrutacji?
                        </h3>
                        <p className="text-sm text-gray-600">Video • 12 min</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                        <Play className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center space-x-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                      <FileText className="h-8 w-8 text-[#2bcaca]" />
                      <div className="flex-1">
                        <h3 className="font-medium">Checklista rekrutacyjna</h3>
                        <p className="text-sm text-gray-600">PDF • 2 strony</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center space-x-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                      <Trophy className="h-8 w-8 text-[#2bcaca]" />
                      <div className="flex-1">
                        <h3 className="font-medium">Quiz: Znajdź swój profil</h3>
                        <p className="text-sm text-gray-600">Interaktywny • 5 min</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                        <Play className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="flex items-center space-x-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                      <Users className="h-8 w-8 text-[#2bcaca]" />
                      <div className="flex-1">
                        <h3 className="font-medium">Webinar dla rodziców</h3>
                        <p className="text-sm text-gray-600">Video • 45 min</p>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#2bcaca] text-white hover:bg-[#229999]">
                        <Play className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Wydarzenia i terminy */}
            <TabsContent value="events" className="space-y-6">
              <Card className="border border-gray-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <CalendarDays className="h-5 w-5 text-[#2bcaca]" />
                    <span>Nadchodzące wydarzenia</span>
                  </CardTitle>
                  <CardDescription>
                    Ważne daty rekrutacji, dni otwarte i webinary
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {events.map((event) => (
                      <Card
                        key={event.id}
                        className="border border-gray-200 shadow-sm transition-shadow hover:shadow-md">
                        <CardContent className="p-4">
                          <div className="mb-3 flex items-start justify-between">
                            <h3 className="font-semibold text-gray-900">{event.title}</h3>
                            <Badge
                              variant="outline"
                              className={
                                event.type === 'Rekrutacja'
                                  ? 'border-[#2bcaca]/20 bg-[#2bcaca]/10 text-[#2bcaca]'
                                  : event.type === 'Dzień otwarty'
                                    ? 'border-green-200 bg-green-50 text-green-700'
                                    : 'border-purple-200 bg-purple-50 text-purple-700'
                              }>
                              {event.type}
                            </Badge>
                          </div>

                          <div className="mb-4 space-y-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <Clock className="mr-2 h-4 w-4" />
                              {new Date(event.date).toLocaleDateString('pl-PL')}
                            </div>
                            {event.location && (
                              <div className="flex items-center text-sm text-gray-600">
                                <MapPin className="mr-2 h-4 w-4" />
                                {event.location}
                              </div>
                            )}
                            {event.school && (
                              <div className="flex items-center text-sm text-gray-600">
                                <Building className="mr-2 h-4 w-4" />
                                {event.school}
                              </div>
                            )}
                          </div>

                          <div className="flex space-x-2">
                            {event.url ? (
                              <Button
                                size="sm"
                                className="flex-1 bg-[#2bcaca] text-white hover:bg-[#229999]">
                                Dołącz online
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                className="flex-1 bg-[#2bcaca] text-white hover:bg-[#229999]">
                                Zobacz szczegóły
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-200">
                              <CalendarDays className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </DashboardLayout>
  )
}
